import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../aws/AuthContext';
import { Layout, Button, Typography, Row, Col, Card, Space, Spin, Grid, Form, Badge, Input, message } from 'antd'; // 👈 Se agregó 'message' para las alertas
import {
  ShopOutlined, CloudSyncOutlined, SecurityScanOutlined, CheckCircleFilled, WalletOutlined, TeamOutlined,
  BarChartOutlined, RocketOutlined, FacebookOutlined, InstagramOutlined, TikTokOutlined, WhatsAppOutlined
} from '@ant-design/icons';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const URLS_LOGOS = {
  pse: '/img/logos_pagos/pse.png',
  nequi: '/img/logos_pagos/nequi.png',
  bancolombia: '/img/logos_pagos/bancolombia.png',
  visa: '/img/logos_pagos/visa.svg',
  mastercard: '/img/logos_pagos/mastercard.png',
  aws: '/img/logos_pagos/aws.png',
  wompi: '/img/logos_pagos/wompi.jpg',
};

const estiloLogo = {
  height: '22px',
  objectFit: 'contain'
};

const FadeInSection = ({ children, delay = 0, direction = 'left' }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    });

    const currentRef = domRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  let initialTransform = 'translateX(-40px)';
  if (direction === 'up') initialTransform = 'translateY(20px)';
  if (direction === 'none') initialTransform = 'translate(0)';

  return (
    <div
      ref={domRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0)' : initialTransform,
        transition: `opacity 0.8s ease-out ${delay}ms, transform 0.8s ease-out ${delay}ms`,
        willChange: 'opacity, transform',
        width: '100%'
      }}
    >
      {children}
    </div>
  );
};

const Home = () => {

  const [form] = Form.useForm();
  const [isHovered, setIsHovered] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/ventas" replace />;
  }

  const onFinish = async (values) => {
    setEnviando(true);
    
    try {

      const response = await fetch(`${API_BASE_URL}/v1-enviar-demo-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });

      if (!response.ok) throw new Error('Error en la petición');

      message.success('¡Gracias! Tus datos han sido enviados. Te contactaremos pronto.');
      form.resetFields(); 
    } catch (error) {
      console.error(error);
      message.error('Hubo un error al enviar tu solicitud. Intenta contactarnos por WhatsApp.');
    } finally {
      setEnviando(false);
    }
  };

  const PlanItem = ({ text, index = 0, baseDelay = 0 }) => (
    <FadeInSection delay={baseDelay + (index * 150)} direction="none">
      <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <CheckCircleFilled style={{ color: '#22BABB', fontSize: '18px', marginTop: '2px' }} />
        <span style={{ lineHeight: '1.4', color: '#cbd5e1' }}>{text}</span>
      </li>
    </FadeInSection>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>

      {/* NAVBAR */}
      <Header style={{ background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', padding: '0 50px', position: 'relative', zIndex: 10 }}>
        <div >
          <img src="/img/g1.png" alt="Yugonub Logo" title='Página de inicio' style={{ cursor: 'pointer', width: !isMobile ? "180px" : "220px", height: !isMobile ? "35px" : "60px" }} />
        </div>
        <Space size="middle">
          <Link to="/login">
            <Button type="text" style={{ fontWeight: '600' }}>Ingresar</Button>
          </Link>
          <Link to="/registro">
            <Button type="primary" style={{ backgroundColor: '#22BABB', borderRadius: '6px', fontWeight: 'bold' }}>
              Registrar mi Negocio
            </Button>
          </Link>
        </Space>
      </Header>

      <Content>
        {/* HERO SECTION CON VIDEO DE FONDO */}
        <div style={{
          position: 'relative',
          paddingTop: isMobile ? '40px' : '80px',
          paddingBottom: isMobile ? '80px' : '190px',
          paddingLeft: isMobile ? '20px' : '50px',
          paddingRight: isMobile ? '20px' : '50px',
          overflow: 'hidden'
        }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            poster="/img/img_capt.png"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0
            }}
          >
            <source src="https://yugonub-assets-public.s3.us-east-1.amazonaws.com/pos-background.mp4" type="video/mp4" />
          </video>

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.75) 0%, rgba(15, 23, 42, 0.50) 100%)',
            zIndex: 1
          }}></div>

          <Row gutter={[48, isMobile ? 40 : 32]} align="middle" style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>

            {/* COLUMNA IZQUIERDA: TEXTO PERSUASIVO */}
            <Col xs={24} lg={13} style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <Space direction="vertical" size={isMobile ? 'middle' : 'large'}>
                <Badge
                  count="Nuevo: Módulo de Inventario Móvil"
                  style={{ backgroundColor: '#22BABB', padding: '0 12px', height: '28px', lineHeight: '28px', borderRadius: '14px' }}
                />
                <FadeInSection>
                  <Title style={{
                    fontSize: isMobile ? '2.2rem' : '3.5rem',
                    lineHeight: '1.1',
                    color: '#ffffff',
                    margin: 0
                  }}>
                    Toma el control total de tu negocio <span style={{ color: '#22BABB' }}>desde cualquier lugar.</span>
                  </Title>
                </FadeInSection>
                <Paragraph style={{
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  color: '#cbd5e1',
                  maxWidth: '550px',
                  margin: isMobile ? '0 auto' : '0'
                }}>
                  Simplifica tus ventas, gestiona inventarios y audita tu caja en tiempo real con el sistema POS más intuitivo de Colombia.
                </Paragraph>

                <div style={{
                  display: 'flex',
                  gap: isMobile ? '15px' : '20px',
                  flexWrap: 'wrap',
                  justifyContent: isMobile ? 'center' : 'flex-start'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleFilled style={{ color: '#22BABB' }} />
                    <Text strong style={{ color: '#ffffff' }}>Sin contratos fijos</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleFilled style={{ color: '#22BABB' }} />
                    <Text strong style={{ color: '#ffffff' }}>Prueba gratis de 10 días</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleFilled style={{ color: '#22BABB' }} />
                    <Text strong style={{ color: '#ffffff' }}>Soporte prioritario</Text>
                  </div>
                </div>
              </Space>
            </Col>

            {/* COLUMNA DERECHA: FORMULARIO */}
            <Col xs={24} lg={11}>
              <Card
                bordered={false}
                style={{
                  borderRadius: '20px',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  padding: isMobile ? '10px' : '20px'
                }}
              >
                <Title level={3} style={{ textAlign: 'center', marginBottom: '25px', fontSize: isMobile ? '1.4rem' : '1.8rem' }}>
                  Impulsa tu negocio hoy
                </Title>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                  <Form.Item name="nombre" rules={[{ required: true, message: 'Por favor ingresa tu nombre' }]}>
                    <Input size="large" placeholder="Nombre completo" style={{ borderRadius: '8px', height: '48px' }} />
                  </Form.Item>

                  <Form.Item name="whatsapp" rules={[{ required: true, message: 'Necesitamos tu contacto' }]}>
                    <Input size="large" placeholder="Tu WhatsApp" style={{ borderRadius: '8px', height: '48px' }} />
                  </Form.Item>

                  <Form.Item name="negocio">
                    <Input size="large" placeholder="Tipo de negocio (Ej: Supermercado)" style={{ borderRadius: '8px', height: '48px' }} />
                  </Form.Item>

                  <Button
                    type="primary" htmlType="submit" size="large" block icon={<RocketOutlined />} title='Solicitar información'
                    loading={enviando}
                    style={{ height: '55px', borderRadius: '8px', backgroundColor: '#22BABB', border: 'none', fontSize: '16px', fontWeight: 'bold', marginTop: '10px' }}
                  >
                    {enviando ? 'ENVIANDO...' : 'SOLICITAR INFORMACIÓN'}
                  </Button>
                </Form>
              </Card>
            </Col>
          </Row>

          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            overflow: 'hidden',
            lineHeight: 0,
            zIndex: 2
          }}>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" style={{
              position: 'relative',
              display: 'block',
              width: 'calc(100% + 1.3px)',
              height: isMobile ? '50px' : '100px'
            }}>
              <path
                d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C51.32,71.18,99,61.07,151,55.44c61.64-6.68,123.28-4.08,184.85,3.64Z"
                fill="#ffffff"
              ></path>
            </svg>
          </div>
        </div>

        {/* CARACTERÍSTICAS */}
        <div style={{
          position: 'relative',
          background: '#fff',
          padding: isMobile ? '80px 20px 100px 20px' : '80px 50px 160px 50px',
          overflow: 'hidden'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
            <FadeInSection>
              <Title style={{ textAlign: 'center', marginBottom: '50px' }}>
                Todo lo que necesitas para potenciar tu negocio
              </Title>
            </FadeInSection>

            <Row gutter={[32, 32]} justify="center">
              <Col xs={24} md={8}>
                <FadeInSection delay={100}>
                  <Card bordered={false} className="feature-card" style={{ textAlign: 'center', background: '#f8fafc', borderRadius: '12px', height: '100%' }}>
                    <ShopOutlined style={{ fontSize: '40px', color: '#22BABB', marginBottom: '20px' }} />
                    <Title level={4}>Caja Rápida y Segura</Title>
                    <Text type="secondary">
                      Vende en segundos con una interfaz intuitiva. Realiza arqueos precisos y cierres de turno sin descuadres de dinero.
                    </Text>
                  </Card>
                </FadeInSection>
              </Col>

              <Col xs={24} md={8}>
                <FadeInSection delay={250}>
                  <Card bordered={false} className="feature-card" style={{ textAlign: 'center', background: '#f8fafc', borderRadius: '12px', height: '100%' }}>
                    <CloudSyncOutlined style={{ fontSize: '40px', color: '#22BABB', marginBottom: '20px' }} />
                    <Title level={4}>100% en la Nube</Title>
                    <Text type="secondary">
                      Tu información siempre segura y disponible. Revisa las ventas de tu local en tiempo real desde tu casa o celular.
                    </Text>
                  </Card>
                </FadeInSection>
              </Col>

              <Col xs={24} md={8}>
                <FadeInSection delay={400}>
                  <Card bordered={false} className="feature-card" style={{ textAlign: 'center', background: '#f8fafc', borderRadius: '12px', height: '100%' }}>
                    <SecurityScanOutlined style={{ fontSize: '40px', color: '#22BABB', marginBottom: '20px' }} />
                    <Title level={4}>Control Total de Personal</Title>
                    <Text type="secondary">
                      Tú decides qué puede ver cada empleado. Oculta costos de compra, restringe anulaciones y protege tu rentabilidad.
                    </Text>
                  </Card>
                </FadeInSection>
              </Col>
            </Row>

            <Row gutter={[32, 32]} justify="center" style={{ marginTop: '32px' }}>
              <Col xs={24} md={8}>
                <FadeInSection delay={100}>
                  <Card bordered={false} className="feature-card" style={{ textAlign: 'center', background: '#f8fafc', borderRadius: '12px', height: '100%' }}>
                    <BarChartOutlined style={{ fontSize: '40px', color: '#22BABB', marginBottom: '20px' }} />
                    <Title level={4}>Informes Inteligentes</Title>
                    <Text type="secondary">
                      Analiza la salud financiera con reportes automáticos. Conoce tus productos más vendidos y tus ganancias reales al instante.
                    </Text>
                  </Card>
                </FadeInSection>
              </Col>

              <Col xs={24} md={8}>
                <FadeInSection delay={250}>
                  <Card bordered={false} className="feature-card" style={{ textAlign: 'center', background: '#f8fafc', borderRadius: '12px', height: '100%' }}>
                    <WalletOutlined style={{ fontSize: '40px', color: '#22BABB', marginBottom: '20px' }} />
                    <Title level={4}>Egresos y Proveedores</Title>
                    <Text type="secondary">
                      Domina tus salidas de dinero. Registra compras de mercancía y gastos diarios para mantener tu flujo de caja bajo control.
                    </Text>
                  </Card>
                </FadeInSection>
              </Col>

              <Col xs={24} md={8}>
                <FadeInSection delay={400}>
                  <Card bordered={false} className="feature-card" style={{ textAlign: 'center', background: '#f8fafc', borderRadius: '12px', height: '100%' }}>
                    <TeamOutlined style={{ fontSize: '40px', color: '#22BABB', marginBottom: '20px' }} />
                    <Title level={4}>Gestión de Clientes y Cartera</Title>
                    <Text type="secondary">
                      Fideliza a tus compradores y gestiona cuentas por cobrar (fiados). Mantén un historial detallado de cada cliente.
                    </Text>
                  </Card>
                </FadeInSection>
              </Col>
            </Row>
          </div>

          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            overflow: 'hidden',
            lineHeight: 0,
            zIndex: 1
          }}>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" style={{
              position: 'relative',
              display: 'block',
              width: 'calc(100% + 1.3px)',
              height: isMobile ? '60px' : '120px',
              transform: 'rotateY(180deg)'
            }}>
              <path
                d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C51.32,71.18,99,61.07,151,55.44c61.64-6.68,123.28-4.08,184.85,3.64Z"
                fill="#0f172a"
              ></path>
            </svg>
          </div>
        </div>

        {/* PLANES (Pricing) */}
        <div style={{ padding: '80px 20px', background: '#0f172a', color: '#fff', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px', position: 'relative', zIndex: 2 }}>
            <FadeInSection>
              <Title level={2} style={{ color: '#fff', marginBottom: '16px', fontSize: isMobile ? '2rem' : '2.5rem' }}>
                Planes diseñados para tu crecimiento
              </Title>
              <Text style={{ color: '#94a3b8', fontSize: '18px', maxWidth: '600px', display: 'inline-block' }}>
                Todo tu negocio en la nube, con el respaldo tecnológico que necesitas para escalar.
              </Text>
            </FadeInSection>
          </div>

          <Row gutter={[32, 32]} justify="center" align="stretch" style={{ maxWidth: '1200px', margin: '0 auto' }}>

            {/* PLAN BÁSICO */}
            <Col xs={24} md={12} lg={9}>
              <FadeInSection delay={100}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    borderRadius: '20px',
                    border: '1px solid #334155',
                    background: '#1e293b',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  styles={{ body: { padding: '40px 30px', flex: 1, display: 'flex', flexDirection: 'column' } }}
                >
                  <div style={{ flex: 1 }}>
                    <Title level={4} style={{ color: '#fff', margin: 0 }}>Básico</Title>
                    <Text style={{ color: '#94a3b8', fontSize: '14px' }}>Ideal para negocios que recién empiezan</Text>

                    <div style={{ margin: '30px 0' }}>
                      <Title level={1} style={{ color: '#fff', margin: 0, fontSize: '48px' }}>
                        $35.000<span style={{ fontSize: '18px', color: '#94a3b8', fontWeight: '400' }}>/mes</span>
                      </Title>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 30px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <PlanItem text={<span>Hasta <strong>2 usuarios</strong> en el sistema</span>} />
                      <PlanItem text={<span><strong>1 Caja</strong> registradora habilitada</span>} />
                      <PlanItem text={<span><strong>Módulos:</strong> Ventas, Caja, Productos, Inventario, Gastos, Compras, Informes y Clientes</span>} />
                      <PlanItem text="Informes de desempeño básicos" />
                      <PlanItem text="Respaldo automático en la nube" />
                      <PlanItem text="Soporte técnico incluido" />
                    </ul>
                  </div>

                  <Link to="/registro?plan=basico">
                    <Button block size="large" style={{ borderRadius: '10px', height: '50px', fontWeight: '700', background: 'transparent', color: '#fff', borderColor: '#475569' }}>
                      COMENZAR PRUEBA GRATIS
                    </Button>
                  </Link>
                </Card>
              </FadeInSection>
            </Col>

            {/* PLAN PREMIUM */}
            <Col xs={24} md={12} lg={9}>
              <FadeInSection delay={250}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    borderRadius: '20px',
                    border: '2px solid #22BABB',
                    background: '#1e293b',
                    position: 'relative',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  styles={{ body: { padding: '40px 30px', flex: 1, display: 'flex', flexDirection: 'column' } }}
                >
                  <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', background: '#22BABB', color: '#fff', padding: '4px 20px', borderRadius: '20px', fontSize: '12px', fontWeight: '900', letterSpacing: '1px' }}>
                    MÁS POPULAR
                  </div>

                  <div style={{ flex: 1 }}>
                    <Title level={4} style={{ color: '#fff', margin: 0 }}>Premium</Title>
                    <Text style={{ color: '#94a3b8', fontSize: '14px' }}>Para comercios en expansión y control total</Text>

                    <div style={{ margin: '30px 0' }}>
                      <Title level={1} style={{ color: '#22BABB', margin: 0, fontSize: '48px' }}>
                        $59.000<span style={{ fontSize: '18px', color: '#94a3b8', fontWeight: '400' }}>/mes</span>
                      </Title>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 30px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <PlanItem text={<span>Hasta <strong>6 usuarios</strong> (Admin + 5 Empleados)</span>} />
                      <PlanItem text={<span><strong>Múltiples Cajas</strong> abiertas simultáneamente</span>} />
                      <PlanItem text={<span><strong>Todos los módulos</strong> del sistema incluidos</span>} />
                      <PlanItem text={<span><strong>Informes avanzados:</strong> Detalles financieros profundos</span>} />
                      <PlanItem text={<span><strong>App Auditoría Móvil:</strong> Cuadra inventarios desde el celular</span>} />
                      <PlanItem text="Gestión de roles y permisos granulares" />
                      <PlanItem text="Soporte técnico prioritario" />
                    </ul>
                  </div>

                  <Link to="/registro?plan=premium">
                    <Button block type="primary" size="large" style={{ borderRadius: '10px', height: '50px', fontWeight: '700', backgroundColor: '#22BABB', border: 'none' }}>
                      ELEGIR PLAN PREMIUM
                    </Button>
                  </Link>
                </Card>
              </FadeInSection>
            </Col>
          </Row>
        </div>

        <div style={{ background: '#f8fafc', padding: '70px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <FadeInSection>
              <Title level={2} style={{ textAlign: 'center', marginBottom: '50px', color: '#0f172a' }}>
                Respaldados por los mejores
              </Title>
            </FadeInSection>

            <Row gutter={[48, 48]} justify="center" align="middle">
              <Col xs={24} md={10} style={{ textAlign: 'center' }}>
                <Card bordered={false} style={{ background: 'transparent', boxShadow: 'none' }}>
                  <div style={{ background: '#fff', width: '90px', height: '90px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                    <img src={URLS_LOGOS.aws} alt="AWS Partner" title='Amazon Web Services' style={{ height: '40px', objectFit: 'contain' }} />
                  </div>
                  <Title level={4}>AWS Cloud Partner</Title>
                  <Text type="secondary" style={{ fontSize: '15px' }}>
                    Toda la infraestructura de Yugonub opera bajo la tecnología de <b>Amazon Web Services</b>. Esto te garantiza seguridad de nivel empresarial, copias de respaldo automáticas y un sistema que nunca se cae.
                  </Text>
                </Card>
              </Col>

              <Col xs={24} md={2} style={{ display: isMobile ? 'none' : 'flex', justifyContent: 'center' }}>
                <div style={{ width: '2px', height: '150px', background: '#e2e8f0' }}></div>
              </Col>

              <Col xs={24} md={10} style={{ textAlign: 'center' }}>
                <Card bordered={false} style={{ background: 'transparent', boxShadow: 'none' }}>
                  <div style={{ width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                    <img src={URLS_LOGOS.wompi} alt="Pasarela Wompi" title='Pasarela de Pagos Wompi' style={{ height: '100%', objectFit: 'contain' }} />
                  </div>
                  <Title level={4}>Transacciones Seguras</Title>
                  <Text type="secondary" style={{ fontSize: '15px', display: 'block', marginBottom: '15px' }}>
                    Renueva y gestiona tu plan mensual directamente desde el sistema usando la pasarela de pagos <b>Wompi</b>, respaldada por Bancolombia.
                  </Text>

                  <FadeInSection>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '15px', marginTop: '15px' }}>
                      <img src={URLS_LOGOS.pse} alt="PSE" title='PSE' style={estiloLogo} />
                      <img src={URLS_LOGOS.visa} alt="Visa" title='Visa' style={estiloLogo} />
                      <img src={URLS_LOGOS.mastercard} alt="Mastercard" title='Mastercard' style={estiloLogo} />
                      <img src={URLS_LOGOS.nequi} alt="Nequi" title='Nequi' style={estiloLogo} />
                      <img src={URLS_LOGOS.bancolombia} alt="Bancolombia" title='Bancolombia' style={estiloLogo} />
                    </div>
                  </FadeInSection>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </Content>

      <Footer style={{ background: '#ffffff', padding: '50px 20px 20px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Row gutter={[32, 32]} justify="space-between" align="middle" style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '30px', marginBottom: '20px' }}>

            <Col xs={24} md={8} style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <img src="/img/g1.png" alt="Yugonub Logo" style={{ height: '35px', marginBottom: '15px' }} />
              <Paragraph type="secondary" style={{ fontSize: '14px', maxWidth: '300px', margin: isMobile ? '0 auto' : '0' }}>
                Tu aliado tecnológico para la administración y facturación POS. Diseñado para escalar contigo.
              </Paragraph>
            </Col>

            <Col xs={24} md={8} style={{ textAlign: 'center' }}>
              <Title level={5} style={{ color: '#475569', marginBottom: '20px' }}>Síguenos y únete a la comunidad</Title>
              <Space size="large">
                <a href="https://www.instagram.com/yugonubcol" title='Instagram' target="_blank" rel="noreferrer"
                  style={{ color: '#94a3b8', fontSize: '28px', transition: 'color 0.3s' }}
                  onMouseOver={e => e.currentTarget.style.color = '#E4405F'}
                  onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}>
                  <InstagramOutlined />
                </a>
                <a href="https://www.tiktok.com/@yugonub" title='TikTok' target="_blank" rel="noreferrer"
                  style={{ color: '#94a3b8', fontSize: '28px', transition: 'color 0.3s' }}
                  onMouseOver={e => e.currentTarget.style.color = '#000000'}
                  onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}>
                  <TikTokOutlined />
                </a>
              </Space>
            </Col>

            <Col xs={24} md={8} style={{ textAlign: isMobile ? 'center' : 'right' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space split={<span style={{ color: '#e2e8f0' }}>|</span>} justify={isMobile ? 'center' : 'end'} style={{ width: '100%' }}>
                  <Link to="/terminos-y-condiciones" style={{ color: '#64748b', fontWeight: '500' }}>Términos y Condiciones</Link>
                  <Link to="/politica-privacidad" style={{ color: '#64748b', fontWeight: '500' }}>Privacidad</Link>
                </Space>
              </Space>
            </Col>
          </Row>

          <div style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: '14px', display: 'block', color: '#64748b' }}>
              Hecho con ❤️ por el equipo de <span style={{ color: '#22BABB', fontWeight: 'bold' }}>YUGONUB</span> en Colombia.
            </Text>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '5px', display: 'block' }}>
              © {new Date().getFullYear()} Yugonub S.A.S. Todos los derechos reservados.
            </Text>
          </div>
        </div>
      </Footer>

      <a
        href="https://wa.me/573147787028?text=Hola,%20quiero%20más%20información%20sobre%20los%20planes%20de%20Yugonub%20-%20POS"
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: 'fixed',
          bottom: isMobile ? '20px' : '30px',
          right: isMobile ? '20px' : '40px',
          width: isMobile ? '55px' : '65px',
          height: isMobile ? '55px' : '65px',
          backgroundColor: '#25D366',
          color: '#FFF',
          borderRadius: '50%',
          boxShadow: '0px 8px 15px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          transform: isHovered ? 'scale(1.1)' : 'scale(1)'
        }}
      >
        <WhatsAppOutlined style={{ fontSize: isMobile ? '30px' : '38px' }} />
      </a>

    </Layout>
  );
}

export default Home;